import { useState, useCallback, useRef } from 'react';

/**
 * useToast — manages a queue of dismissible toast notifications.
 *
 * Each toast: { id, type, title, message, duration }
 * type: 'badge' | 'streak' | 'achievement' | 'success' | 'info'
 */
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});

  const dismiss = useCallback((id) => {
    if (timerRefs?.current?.[id]) {
      clearTimeout(timerRefs?.current?.[id]);
      delete timerRefs?.current?.[id];
    }
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const addToast = useCallback(
    ({ type = 'info', title, message, duration = 4500 }) => {
      const id = `toast-${Date.now()}-${Math.random()?.toString(36)?.slice(2, 7)}`;
      setToasts((prev) => [...prev, { id, type, title, message }]);

      timerRefs.current[id] = setTimeout(() => {
        dismiss(id);
      }, duration);
    },
    [dismiss]
  );

  const toastBadge = useCallback(
    (badgeName) => {
      addToast({
        type: 'badge',
        title: '🏅 Badge Unlocked!',
        message: `You earned the "${badgeName}" badge!`,
        duration: 5000,
      });
    },
    [addToast]
  );

  const toastStreak = useCallback(
    (streakDays) => {
      addToast({
        type: 'streak',
        title: '🔥 Streak Milestone!',
        message: `${streakDays}-day streak reached — keep it up!`,
        duration: 5000,
      });
    },
    [addToast]
  );

  const toastAchievement = useCallback(
    (achievementName) => {
      addToast({
        type: 'achievement',
        title: '🎖️ Achievement Unlocked!',
        message: `You unlocked "${achievementName}"!`,
        duration: 5000,
      });
    },
    [addToast]
  );

  return { toasts, addToast, toastBadge, toastStreak, toastAchievement, dismiss };
};

export default useToast;
